/**
 * Chart.js configuration utilities with theme-aware presets and RTL support
 * @module lib/utils/chart-config
 */

import type { ChartOptions } from 'chart.js';

/**
 * Theme colors for charts
 */
export interface ChartThemeColors {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  background: string;
  text: string;
  grid: string;
  tooltip: {
    background: string;
    text: string;
    border: string;
  };
}

/**
 * Light theme colors
 */
const lightThemeColors: ChartThemeColors = {
  primary: 'rgb(59, 130, 246)', // blue-500
  secondary: 'rgb(168, 85, 247)', // purple-500
  success: 'rgb(34, 197, 94)', // green-500
  warning: 'rgb(251, 191, 36)', // amber-400
  danger: 'rgb(239, 68, 68)', // red-500
  info: 'rgb(20, 184, 166)', // teal-500
  background: 'rgb(255, 255, 255)', // white
  text: 'rgb(15, 23, 42)', // slate-900
  grid: 'rgb(226, 232, 240)', // slate-200
  tooltip: {
    background: 'rgb(30, 41, 59)', // slate-800
    text: 'rgb(248, 250, 252)', // slate-50
    border: 'rgb(71, 85, 105)', // slate-600
  },
};

/**
 * Dark theme colors
 */
const darkThemeColors: ChartThemeColors = {
  primary: 'rgb(96, 165, 250)', // blue-400
  secondary: 'rgb(192, 132, 252)', // purple-400
  success: 'rgb(74, 222, 128)', // green-400
  warning: 'rgb(252, 211, 77)', // amber-300
  danger: 'rgb(248, 113, 113)', // red-400
  info: 'rgb(45, 212, 191)', // teal-400
  background: 'rgb(15, 23, 42)', // slate-900
  text: 'rgb(241, 245, 249)', // slate-100
  grid: 'rgb(51, 65, 85)', // slate-700
  tooltip: {
    background: 'rgb(241, 245, 249)', // slate-100
    text: 'rgb(15, 23, 42)', // slate-900
    border: 'rgb(148, 163, 184)', // slate-400
  },
};

/**
 * Get theme colors based on current theme
 */
export function getThemeColors(isDark: boolean): ChartThemeColors {
  return isDark ? darkThemeColors : lightThemeColors;
}

/**
 * Get chart.js color palette for datasets
 */
export function getChartColorPalette(
  isDark: boolean
): [string, string, string, string, string, string] {
  const colors = getThemeColors(isDark);
  return [
    colors.primary,
    colors.secondary,
    colors.success,
    colors.warning,
    colors.danger,
    colors.info,
  ];
}

/**
 * Base chart configuration with theme support
 */
export function getBaseChartConfig(
  isDark: boolean,
  isRTL: boolean
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> {
  const colors = getThemeColors(isDark);

  return {
    responsive: true,
    maintainAspectRatio: true,
    rtl: isRTL,
    plugins: {
      legend: {
        position: isRTL ? 'right' : 'left',
        labels: {
          color: colors.text,
          padding: 16,
          font: {
            size: 12,
            family: isRTL ? 'Tajawal, sans-serif' : 'Poppins, sans-serif',
          },
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: colors.tooltip.background,
        titleColor: colors.tooltip.text,
        bodyColor: colors.tooltip.text,
        borderColor: colors.tooltip.border,
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        titleFont: {
          size: 14,
          weight: 'bold',
          family: isRTL ? 'Tajawal, sans-serif' : 'Poppins, sans-serif',
        },
        bodyFont: {
          size: 13,
          family: isRTL ? 'Tajawal, sans-serif' : 'Poppins, sans-serif',
        },
        rtl: isRTL,
      },
    },
    scales: {
      x: {
        border: { display: false },
        grid: {
          color: colors.grid,
        },
        ticks: {
          color: colors.text,
          font: {
            size: 11,
            family: isRTL ? 'Tajawal, sans-serif' : 'Poppins, sans-serif',
          },
        },
        reverse: isRTL,
      },
      y: {
        border: { display: false },
        grid: {
          color: colors.grid,
        },
        ticks: {
          color: colors.text,
          font: {
            size: 11,
            family: isRTL ? 'Tajawal, sans-serif' : 'Poppins, sans-serif',
          },
        },
        position: isRTL ? 'right' : 'left',
      },
    },
  };
}

/**
 * Line chart configuration preset
 */
export function getLineChartConfig(
  isDark: boolean,
  isRTL: boolean,
  aspectRatio = 2
): ChartOptions<'line'> {
  return {
    ...getBaseChartConfig(isDark, isRTL),
    aspectRatio,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 2,
      },
      point: {
        radius: 3,
        hoverRadius: 5,
        hitRadius: 10,
      },
    },
  };
}

/**
 * Bar chart configuration preset
 */
export function getBarChartConfig(
  isDark: boolean,
  isRTL: boolean,
  aspectRatio = 2
): ChartOptions<'bar'> {
  return {
    ...getBaseChartConfig(isDark, isRTL),
    aspectRatio,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      bar: {
        borderWidth: 0,
        borderRadius: 4,
      },
    },
  };
}

/**
 * Doughnut chart configuration preset
 */
export function getDoughnutChartConfig(
  isDark: boolean,
  isRTL: boolean,
  aspectRatio = 2
): ChartOptions<'doughnut'> {
  const baseConfig = getBaseChartConfig(isDark, isRTL);

  // Remove scales for doughnut charts
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { scales: _scales, ...configWithoutScales } = baseConfig;

  return {
    ...configWithoutScales,
    aspectRatio,
    cutout: '70%',
    plugins: {
      ...baseConfig.plugins,
      legend: {
        ...baseConfig.plugins?.legend,
        position: 'bottom',
      },
    },
  };
}

/**
 * Generate gradient background for charts
 */
export function createGradient(
  ctx: CanvasRenderingContext2D,
  color: string,
  opacity = 0.1
): CanvasGradient {
  const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
  gradient.addColorStop(0, color.replace('rgb', 'rgba').replace(')', `, ${opacity})`));
  gradient.addColorStop(1, color.replace('rgb', 'rgba').replace(')', ', 0)'));
  return gradient;
}

/**
 * Format number with locale-specific formatting
 */
export function formatChartNumber(
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions
): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(value);
}

/**
 * Format currency for chart tooltips
 */
export function formatChartCurrency(
  value: number,
  locale: string,
): string {
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return `NC ${formatted}`;
}

/**
 * Get responsive aspect ratio based on screen width
 */
export function getResponsiveAspectRatio(width: number): number {
  if (width < 640) return 1; // Mobile
  if (width < 1024) return 1.5; // Tablet
  return 2; // Desktop
}
